import styled from "@emotion/styled";
import { useCallback, useMemo } from "react";

import {
  FONT_WEIGHT_NORMAL,
  FONT_WEIGHT_SEMI_BOLD,
  FieldSwitch,
  FormRowField,
  FormikForm,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { FormBody, Header, Heading, PageContainer, StyledForm, SwitchLabel } from "../../shared";
import { useCreateModuleSettings } from "../CreateModuleSettingsContext";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;

  width: 100%;

  font-size: 15.6px;
`;

const ItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 13px;

  width: 100%;
`;

const Title = styled.span`
  color: ${({ theme }) => theme.generalTextIcon};

  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  line-height: 18.9px;
`;

const Description = styled.span`
  color: ${({ theme }) => theme.generalTextIcon};

  font-weight: ${FONT_WEIGHT_NORMAL};
`;

type FormValues = {
  understood: boolean;
};

export const SummaryPage = () => {
  const formatMessage = useFormatMessage(formatStrings);

  const { setUnderstood } = useCreateModuleSettings();

  const initialValues: FormValues = useMemo(() => ({ understood: false }), []);

  const handleSubmit = useCallback((values: FormValues) => setUnderstood(values.understood), [setUnderstood]);

  return (
    <PageContainer>
      <Header>
        <Heading>{formatMessage("heading")}</Heading>
      </Header>

      <Container>
        <ItemContainer>
          <Title>{formatMessage("tasksTitle")}</Title>
          <Description>{formatMessage("tasksDescription")}</Description>
        </ItemContainer>

        <ItemContainer>
          <Title>{formatMessage("progressTitle")}</Title>
          <Description>{formatMessage("progressDescription")}</Description>
        </ItemContainer>

        <ItemContainer>
          <Title>{formatMessage("inChargeTitle")}</Title>
          <Description>{formatMessage("inChargeDescription")}</Description>
        </ItemContainer>
      </Container>

      <FormikForm<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitOnBlur
        formatStrings={formatStrings}
        dataTest="summary-form"
      >
        <StyledForm>
          <FormBody>
            <FormRowField<FormValues> name="understood">
              <FieldSwitch label={<SwitchLabel>{formatMessage("understoodLabel")}</SwitchLabel>} disabled />
            </FormRowField>
          </FormBody>
        </StyledForm>
      </FormikForm>
    </PageContainer>
  );
};

const formatStrings = {
  heading: {
    en: "Working with AI",
  },
  tasksTitle: {
    en: "Delegating tasks",
  },
  tasksDescription: {
    en: "Your assistant works in parallel to you.  When you delegate a task, you can keep working without your assistant getting in the way.",
  },
  progressTitle: {
    en: "Checking on progress",
  },
  progressDescription: {
    en: "Your assistant will let you know when something is ready for you to review.  You can also check on progress at any time.",
  },
  inChargeTitle: {
    en: "You're in charge",
  },
  inChargeDescription: {
    en: "Nothing your assistant works on becomes part of your module without your approval.  Once approved, you can edit as you like.",
  },
  understoodLabel: {
    en: "Understood.  Don't remind me again.",
  },
} satisfies FormatMessages;
